import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  RefreshCw, 
  Search, 
  Clock, 
  ShieldCheck, 
  UserCheck, 
  Activity, 
  Sparkles,
  AlertCircle,
  Eye
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ConnectedUser {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  company: string;
  avatar_url?: string;
  current_page: string;
  is_admin: boolean;
  seconds_ago: number;
}

interface PresenceResponse {
  total_connected: number;
  admin_count: number;
  user_count: number;
  timestamp: string;
  users: ConnectedUser[];
}

export default function AdminOnlineUsers() {
  const { profile } = useAuth();
  const apiClient = useApiClient();
  const [data, setData] = useState<PresenceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  const fetchOnlineUsers = async () => {
    try {
      setLoading(true);
      const res = await (apiClient as any).get("/api/v1/admin/online-users");
      setData(res);
    } catch (err) {
      console.error("Error al obtener usuarios conectados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const timer = setInterval(fetchOnlineUsers, 10000); // Refresco cada 10s
    return () => clearInterval(timer);
  }, []);

  if (profile?.role !== "admin") {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full border-red-200 bg-red-50/40 p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Acceso Restringido</h2>
          <p className="text-sm text-slate-600">
            Esta sección es exclusiva para usuarios con privilegios de Administrador.
          </p>
        </Card>
      </div>
    );
  }

  const filteredUsers = (data?.users || []).filter((u) => {
    const matchesSearch = 
      (u.full_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (u.email?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (u.company?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    
    if (roleFilter === "admin") return matchesSearch && (u.is_admin || u.role === "admin");
    if (roleFilter === "lawyer") return matchesSearch && u.role === "lawyer";
    if (roleFilter === "client") return matchesSearch && u.role !== "admin" && u.role !== "lawyer";
    return matchesSearch;
  });

  const getRoleBadge = (role: string, isAdmin: boolean) => {
    if (isAdmin || role === "admin") {
      return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300 font-bold">Admin</Badge>;
    }
    if (role === "lawyer") {
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300 font-bold">Abogado</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium">{role || "Cliente"}</Badge>;
  };

  return (
    <div className="p-6 md:p-8 space-y-6 bg-slate-50/50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-md shadow-blue-600/20">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                  Usuarios Conectados
                </h1>
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium">
                Monitoreo en vivo de actividad y sesiones activas en la plataforma.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOnlineUsers}
            disabled={loading}
            className="rounded-xl gap-2 font-bold border-slate-200 shadow-sm bg-white hover:bg-slate-50">
            <RefreshCw className={loading ? "h-4 w-4 animate-spin text-blue-600" : "h-4 w-4 text-blue-600"} />
            Sincronizar
          </Button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-slate-200 bg-white shadow-sm p-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Conectados</p>
            <h3 className="text-3xl font-black text-slate-900 mt-1">{data?.total_connected ?? 0}</h3>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
            <UserCheck className="h-6 w-6" />
          </div>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-sm p-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Administradores</p>
            <h3 className="text-3xl font-black text-purple-600 mt-1">{data?.admin_count ?? 0}</h3>
          </div>
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl border border-purple-100">
            <ShieldCheck className="h-6 w-6" />
          </div>
        </Card>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-sm p-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Abogados / Clientes</p>
            <h3 className="text-3xl font-black text-blue-600 mt-1">{data?.user_count ?? 0}</h3>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
            <Activity className="h-6 w-6" />
          </div>
        </Card>
      </div>

      {/* Filter and Search Bar */}
      <Card className="rounded-2xl border-slate-200 bg-white shadow-sm">
        <CardHeader className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, correo o empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-xl border-slate-200 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={roleFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setRoleFilter("all")}
              className="rounded-xl text-xs font-semibold">
              Todos ({data?.total_connected ?? 0})
            </Button>
            <Button
              variant={roleFilter === "admin" ? "default" : "outline"}
              size="sm"
              onClick={() => setRoleFilter("admin")}
              className="rounded-xl text-xs font-semibold">
              Admins ({data?.admin_count ?? 0})
            </Button>
            <Button
              variant={roleFilter === "lawyer" ? "default" : "outline"}
              size="sm"
              onClick={() => setRoleFilter("lawyer")}
              className="rounded-xl text-xs font-semibold">
              Abogados
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-700">No se encontraron usuarios conectados</p>
              <p className="text-xs text-slate-400 mt-1">Los usuarios aparecerán en cuanto interactúen con la plataforma.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map((u) => (
                <div key={u.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between py-3.5 px-3 hover:bg-slate-50 rounded-xl transition-colors gap-3">
                  <div className="flex items-center gap-3.5">
                    <Avatar className="h-10 w-10 border border-slate-200 shadow-sm">
                      <AvatarImage src={u.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-sm">
                        {u.full_name?.slice(0, 2).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-slate-900">{u.full_name}</span>
                        {getRoleBadge(u.role, u.is_admin)}
                      </div>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">
                        {u.email} {u.company && u.company !== "N/A" ? `• ${u.company}` : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center text-xs">
                    <div className="font-bold text-slate-700 flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-emerald-500" />
                      <span>hace {u.seconds_ago < 5 ? "unos segundos" : `${u.seconds_ago} segundos`}</span>
                    </div>
                    <span className="text-[11px] text-slate-400 font-mono mt-0.5 bg-slate-100 px-2 py-0.5 rounded-md">
                      {u.current_page}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
